import { useMemo } from "react";

import type { Options, OptionsStackingValue, PointOptionsObject } from "highcharts";
import { renderToStaticMarkup } from "react-dom/server";
import { styled } from "styled-components";

import { ColumnChartGraph, LEGEND_POSITIONS } from "@similarweb/pro-logic-components";
import { colorsPalettes, fontVariants } from "@similarweb/styles";
import { type ColumnDefinition, ChartTooltip } from "@similarweb/ui-components";
import { Box } from "@similarweb/ui-components/foundations";

import { useSubtitleFilters } from "app/custom-hooks/useSubtitleFilters";
import { abbrNumberFilter, percentageSignFilter } from "app/filters/ngFilters";
import { WidgetContainer } from "app/components/React/WidgetContainer/WidgetContainer";
import { translate } from "app/filters/i18nFilter";

import type { ICategoryComparison } from "../../categories-context/CategoriesContext";

import type { MarketingChannelsData } from "./useMarketingChannelsQueries";

const formatNumber: (value: string | number) => string = abbrNumberFilter();

const formatPercentTooltip = (value: number | string, fraction: number = 0): string => {
    return percentageSignFilter()(value, fraction) as string;
};

type TooltipDataRecord = { value: number; trafficValue: number; custom: { label: string; color: string } };

const tooltipColumnDefinition: Array<ColumnDefinition<TooltipDataRecord>> = [
    {
        key: "custom",
        get title() {
            return translate("market_analysis.marketing_channels.channels_overview.chart_tooltip.industry");
        },
        align: "left",
        selector: "custom",
        size: "200px",
        render: ({ custom }) => (
            <div data-automation="chart-tooltip-label">
                <ChartTooltip.LabelCell
                    label={custom.label}
                    backgroundColor={custom.color}
                />
            </div>
        ),
    },
    {
        key: "value",
        get title() {
            return translate("market_analysis.marketing_channels.channels_overview.chart_tooltip.share");
        },
        align: "right",
        selector: "value",
        size: ChartTooltip.ValueCellDefaultwidth,
        render: ({ value }) => (
            <div data-automation="chart-tooltip-value">
                <ChartTooltip.ValueCell value={formatPercentTooltip(value, 2)} />
            </div>
        ),
    },
    {
        key: "trafficValue",
        get title() {
            return translate("market_analysis.marketing_channels.channels_overview.chart_tooltip.traffic");
        },
        align: "right",
        selector: "trafficValue",
        size: "1.5fr",
        render: ({ trafficValue }) => (
            <div data-automation="chart-tooltip-value">
                <ChartTooltip.ValueCell value={formatNumber(trafficValue)} />
            </div>
        ),
    },
];

// Unfortunately, pro-logic-components doesn't allow to pass the gap as a prop, so we have to override the default value using a styled component.
const ColumnChartWrapper = styled.div`
    .legends-container {
        gap: 16px;
    }
`;

type CategoryId = string;

interface IMarketingChannelsChannelsOverviewWidgetProps {
    isLoading: boolean;
    isError: boolean;
    data: Record<CategoryId, MarketingChannelsData | null>;
    categories: ICategoryComparison[];
}

export const MarketingChannelsChannelsOverviewWidget = ({
    isLoading,
    isError,
    data,
    categories,
}: IMarketingChannelsChannelsOverviewWidgetProps) => {
    const subtitleFilters = useSubtitleFilters("date", "country", "webSource");

    const chartData = useMemo(() => {
        if (!data || !categories.length) {
            return [];
        }

        const firstCategoryData = data[categories[0]?.id];
        if (!firstCategoryData?.channelVolumeList) {
            return [];
        }

        return categories
            .map((category) => {
                const categoryData = data[category.id];

                if (!categoryData?.channelVolumeList) {
                    return null;
                }

                const categoryName = category.category.text ?? category.category.name;

                return {
                    id: category.id,
                    name: categoryName,
                    color: category.color,
                    visible: true,
                    total: categoryData.total,
                    data: categoryData.channelVolumeList.map((channel) => ({
                        name: translate(channel.titleKey) ?? channel.channel,
                        y: channel.share,
                        total: channel.volume,
                        custom: {
                            label: categoryName,
                            color: category.color,
                        },
                    })),
                };
            })
            .filter((item) => item !== null);
    }, [data, categories]);

    const chartConfig = useMemo(
        () =>
            ({
                chart: {
                    type: "column" as const,
                    height: 250,
                },
                plotOptions: {
                    column: {
                        // Highcharts reference doesn't list `null` as allowed value but this is the only way to reset the default library value
                        // TODO: Remove once https://similarweb-rnd.atlassian.net/browse/PFG-251 is resolved
                        stacking: null as unknown as OptionsStackingValue,
                        maxPointWidth: 40,
                        minPointLength: 3,
                        pointPadding: 0.15,
                        dataLabels: {
                            enabled: true,
                            useHTML: true,
                            formatter() {
                                return formatPercentTooltip(this.y, 0);
                            },
                            color: colorsPalettes.carbon[250],
                            style: {
                                fontSize: `${fontVariants.dataTable2.size}px`,
                                fontWeight: `${fontVariants.dataTable2.weight}`,
                                fontFamily: fontVariants.dataTable2.family,
                            },
                        },
                    },
                },
                xAxis: {
                    type: "category",
                },
                yAxis: {
                    minRange: 1,
                    floor: 0,
                    labels: {
                        formatter() {
                            return formatPercentTooltip(this.value, 0);
                        },
                    },
                },
                tooltip: {
                    padding: 0,
                    useHTML: true,
                    outside: false,
                    formatter() {
                        const tooltipData: TooltipDataRecord[] = this.points.map(
                            ({ options }: { options: PointOptionsObject & { total?: number } }) => ({
                                value: options.y,
                                trafficValue: options.total,
                                custom: options.custom as { label: string; color: string },
                            }),
                        );

                        return renderToStaticMarkup(
                            <Box
                                padding="16px"
                                borderRadius="8px"
                                background={colorsPalettes.carbon[0]}
                            >
                                <ChartTooltip
                                    title={this.name}
                                    columnDefinition={tooltipColumnDefinition}
                                    data={tooltipData}
                                />
                            </Box>,
                        );
                    },
                },
            }) satisfies Options,
        [],
    );

    return (
        <WidgetContainer
            withBorder
            isLoading={isLoading}
            noData={isError}
            automationId="marketing-channels-channels-overview-widget"
            title={translate("market_analysis.marketing_channels.channels_overview.title")}
            tooltip={translate("market_analysis.marketing_channels.channels_overview.tooltip")}
            loaderHeight={324}
            loaderWidth="100%"
            subtitleFilters={subtitleFilters}
        >
            <Box padding="32px 24px 24px">
                {chartData.length > 0 ? (
                    <ColumnChartWrapper>
                        <ColumnChartGraph
                            shouldPresentLegend
                            shouldPresentValueAtLegend
                            data={chartData}
                            graphConfig={chartConfig}
                            legendPosition={LEGEND_POSITIONS.TOP}
                            legendValueFilter={formatNumber}
                            legendsWithValueMaxWidth={300}
                        />
                    </ColumnChartWrapper>
                ) : null}
            </Box>
        </WidgetContainer>
    );
};
